// The working location the header shows, and the one the server scopes this
// login's lists to (2026-10-07: the owner's all_departments login picked
// Frisco but still saw Odessa's visits and providers).
//
// Server rule (both gateways, helpers.get_scoped_departmentid):
//   - a location-scoped login sees config.departmentid (untagged = Odessa);
//     PUT /user/switch-department writes it;
//   - an all_departments login may view any location: config.location_scope
//     1 / 2 is the location it picked in the header, "all" (or nothing
//     picked yet) is every location. The switch writes location_scope (and
//     departmentid for 1 / 2; "All locations" keeps departmentid, the EHR
//     department orders, meds and labs use).
// Only all_departments logins are offered "All locations", and only where the
// header switch exists (practiceFeatures().workingLocations, 9928): elsewhere
// (OSA, one department) everything below keeps the stored departmentid.
import { defaultDepartmentId, isSeparateDepartment, practiceDepartments, practiceFeatures } from "../config/practice";

export const ALL_LOCATIONS = "all";

export const canViewAllLocations = (config) =>
  Boolean(config?.all_departments) && Boolean(practiceFeatures().workingLocations);

const offeredIds = () => practiceDepartments().map((d) => String(d.id));

// "1" / "2" / "3" / "all": the header's current value for this login.
export function workingLocation(config) {
  if (canViewAllLocations(config)) {
    const picked = String(config?.location_scope ?? "").trim();
    return offeredIds().includes(picked) ? picked : ALL_LOCATIONS;
  }
  return String(config?.departmentid || defaultDepartmentId());
}

// The locations this login may pick (header) and filter on (Reports, Note
// Review), as practice department entries:
//   - all_departments logins: every location;
//   - a login tagged with a separate location (9928: CODE): only that one
//     (owner 2026-10-27, "CODE staff see only CODE");
//   - any other location-scoped login: every location except the separate
//     ones (Odessa / Frisco staff switch between those two, as before).
export function locationsFor(config) {
  const all = practiceDepartments();
  if (canViewAllLocations(config) || !practiceFeatures().workingLocations) return all;
  const current = workingLocation(config);
  if (isSeparateDepartment(current)) return all.filter((d) => String(d.id) === current);
  return all.filter((d) => !isSeparateDepartment(d.id));
}

// The locations a screen with its own location picker (Reports, Note Review)
// lists for this login: only its own for a login kept to a separate location
// (CODE); every location otherwise (Odessa / Frisco staff could always filter
// on the other location there, and their "All departments" includes CODE).
export function filterLocationsFor(config) {
  const all = practiceDepartments();
  if (filterOffersAll(config)) return all;
  const current = workingLocation(config);
  return all.filter((d) => String(d.id) === current);
}

// Whether a screen with its own location picker offers "All departments" to
// this login: not to a login kept to one separate location (CODE).
export function filterOffersAll(config) {
  if (canViewAllLocations(config) || !practiceFeatures().workingLocations) return true;
  return !isSeparateDepartment(workingLocation(config));
}

export const viewsAllLocations = (config) => workingLocation(config) === ALL_LOCATIONS;

// The location a room created by this login belongs to: the header's location
// ("1" / "2" / "3") where the practice has the switcher; null on "All
// locations" and in a one-location practice (the server keeps its default).
// Sent as create_room's departmentid: rooms are listed per location, and a
// new room landed in Odessa whatever the header showed. The gateways ignore
// the parameter until they read it (PLAN-DEPT3 Step 2, rooms/post_rooms).
export function roomDepartmentId(config) {
  if (!practiceFeatures().workingLocations) return null;
  const loc = workingLocation(config);
  return offeredIds().includes(loc) ? loc : null;
}

// Default of a screen with its own location picker (RPM, Reports, Note
// Review): the header's location as a number, or "" (their "All") when the
// header shows "All locations". A location-scoped login keeps the old
// default exactly: its stored departmentid, "" when none is stored.
export function locationFilterDefault(config) {
  // A login kept to a separate location (CODE) has no "All": its location.
  if (!filterOffersAll(config)) return Number(workingLocation(config));
  const raw = canViewAllLocations(config) ? workingLocation(config) : config?.departmentid;
  if (raw === ALL_LOCATIONS) return "";
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : "";
}

// Body of PUT /user/switch-department for a header choice.
export const switchRequestBody = (target) => ({
  departmentid: target === ALL_LOCATIONS ? ALL_LOCATIONS : Number(target),
});

// The user config after a successful switch, mirroring what the server stored
// (strings, like the server's JSON).
export function configAfterSwitch(config, target) {
  const base = { ...(config || {}) };
  if (target === ALL_LOCATIONS) return { ...base, location_scope: ALL_LOCATIONS };
  const dep = String(target);
  if (canViewAllLocations(base)) return { ...base, departmentid: dep, location_scope: dep };
  return { ...base, departmentid: dep };
}
